import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Badge } from './badge';
import { Check, Search, X } from 'lucide-react';
import { Tooltip } from 'antd';
import { cn } from '@/lib/utils';

export interface ClienteOption {
  id: number;
  nome: string;
  cpf: string | null;
  email: string | null;
  tel1: string | null;
}

interface ClienteAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onClienteSelect?: (cliente: ClienteOption) => void;
  clientes: ClienteOption[];
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
  selectedCliente?: ClienteOption | null;
  onClearSelection?: () => void;
}

export function ClienteAutocomplete({
  value,
  onChange,
  onClienteSelect,
  clientes,
  isLoading = false,
  placeholder = "Digite o nome do cliente",
  className,
  selectedCliente,
  onClearSelection,
}: ClienteAutocompleteProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Mostrar sugestões quando há valor e clientes
  useEffect(() => {
    setShowSuggestions(value.length >= 2 && clientes.length > 0 && !selectedCliente);
  }, [value, clientes, selectedCliente]);

  // Resetar índice destacado quando sugestões mudam
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [clientes]);

  // Fechar sugestões quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(newValue.length >= 2 && clientes.length > 0);
  };

  const handleClienteClick = (cliente: ClienteOption) => {
    onClienteSelect?.(cliente);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || clientes.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < clientes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < clientes.length) {
          handleClienteClick(clientes[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className={cn("relative", className)}>
      {/* Input principal */}
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (value.length >= 2 && clientes.length > 0 && !selectedCliente) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          className={cn(
            "pr-10",
            selectedCliente && "border-green-500 bg-green-50 dark:bg-green-900/10"
          )}
        />

        {/* Indicadores no input */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {selectedCliente && (
            <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          )}

          {isLoading && (
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          )}

          {!isLoading && !selectedCliente && value.length >= 2 && (
            <Search className="h-4 w-4 text-muted-foreground" />
          )}

          {selectedCliente && onClearSelection && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onClearSelection();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Lista de sugestões */}
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {clientes.map((cliente, index) => (
            <Tooltip
              key={cliente.id}
              title={
                <div className="space-y-1.5">
                  <p className="font-medium text-white">{cliente.nome}</p>
                  {cliente.cpf && <p className="text-xs text-gray-200">CPF: {cliente.cpf}</p>}
                  {cliente.email && <p className="text-xs text-gray-200">Email: {cliente.email}</p>}
                  {cliente.tel1 && <p className="text-xs text-gray-200">Telefone: {cliente.tel1}</p>}
                </div>
              }
            >
              <div
                className={cn(
                  "px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700",
                  highlightedIndex === index && "bg-blue-50 dark:bg-blue-900/20"
                )}
                onClick={() => handleClienteClick(cliente)}
              >
                <div className="space-y-2">
                  {/* Nome */}
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {cliente.nome}
                  </p>

                  {/* CPF */}
                  {cliente.cpf && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      CPF: {cliente.cpf}
                    </p>
                  )}

                  {/* Email */}
                  {cliente.email && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      Email: {cliente.email}
                    </p>
                  )}

                  {/* Telefone */}
                  {cliente.tel1 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      Tel: {cliente.tel1}
                    </p>
                  )}
                </div>
              </div>
            </Tooltip>
          ))}

          {clientes.length === 0 && value.length >= 2 && !isLoading && (
            <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
              Nenhum cliente encontrado
            </div>
          )}
        </div>
      )}

      {/* Mensagem quando cliente está selecionado */}

    </div>
  );
}
